// CompanyDetailPage — детальная страница компании с cross-entity вкладками
// и edit-диалогом (R17a, D12: PUT /api/companies/{id} с expectedUpdatedAt).
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Alert,
} from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { CrossEntityTabs, type CrossEntityTab } from './components/CrossEntityTabs';
import { DealStageChip } from './components/DealStageChip';
import { AmountChip } from './components/AmountChip';
import { PaymentBlock } from '@/components/PaymentBlock';
import { companiesApi } from '@/api/companies';
import type { Deal } from '@/api/deals';
import { formatDate } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { isValidWebsite, normalizeWebsite } from '@/lib/website';
import { t } from '@/i18n';

const PAGE_SIZE = 50;

/** Детальная страница компании с cross-entity вкладками. */
export function CompanyDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({
    name: '',
    industry: '',
    website: '',
    address: '',
    taxId: '',
    notes: '',
  });

  const companyQuery = useQuery({
    queryKey: ['company', id],
    queryFn: () => companiesApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      // B2: optimistic-lock требует фактический updatedAt из данных — без fallback «now».
      if (!companyQuery.data) throw new Error('Данные компании не загружены');
      return companiesApi.update(id ?? '', {
        name: editValues.name,
        industry: editValues.industry || null,
        website: editValues.website || null,
        address: editValues.address || null,
        taxId: editValues.taxId,
        notes: editValues.notes || null,
        expectedUpdatedAt: companyQuery.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', id] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      // U-29: селекторы построены на ['companies-map'] — инвалидация имени после rename.
      void queryClient.invalidateQueries({ queryKey: ['companies-map'] });
      setEditOpen(false);
    },
    onError: () => {
      // B2: 409/ошибка → refetch — свежий updatedAt, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const contactsQuery = useQuery({
    queryKey: ['company-contacts', id],
    queryFn: () => companiesApi.listContactsByCompany(id ?? '', 0, PAGE_SIZE),
    enabled: Boolean(id) && activeTab === 'contacts',
  });

  const dealsQuery = useQuery({
    queryKey: ['company-deals', id],
    queryFn: () => companiesApi.listDealsByCompany(id ?? '', 0, PAGE_SIZE),
    enabled: Boolean(id) && activeTab === 'deals',
  });

  if (companyQuery.isLoading) return <LoadingSpinner label="Загрузка компании…" />;
  if (companyQuery.isError || !companyQuery.data) {
    return (
      <EmptyState
        title="Компания не найдена"
        description={companyQuery.error instanceof Error ? companyQuery.error.message : 'Неизвестная ошибка'}
        actionLabel="К списку компаний"
        onAction={() => navigate('/crm/companies')}
      />
    );
  }
  const company = companyQuery.data;

  // U-22: мягкая валидация сайта в edit-форме (схема опциональна, TLD обязателен).
  const websiteError = !isValidWebsite(editValues.website)
    ? t('crm.companies.websiteInvalid')
    : undefined;

  /** Открывает edit-диалог с текущими значениями компании. */
  const openEdit = (): void => {
    setEditValues({
      name: company.name,
      industry: company.industry ?? '',
      website: company.website ?? '',
      address: company.address ?? '',
      taxId: company.taxId ?? '',
      notes: company.notes ?? '',
    });
    setEditOpen(true);
  };

  const tabs: CrossEntityTab[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'contacts', label: 'Контакты' },
    { id: 'deals', label: 'Сделки' },
    // V12 U-35-C: платежи компании (блок PaymentBlock в отдельной вкладке).
    { id: 'payments', label: t('payments.title') },
  ];

  return (
    <Box>
      <PageHeader
        title={company.name}
        subtitle={`ИНН: ${company.taxId ?? '—'}`}
        actions={
          <Button variant="outlined" onClick={openEdit}>
            Редактировать
          </Button>
        }
      />
      {editMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(editMutation.error, 'Не удалось сохранить компанию')}
        </Alert>
      ) : null}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Отрасль
                </Typography>
                <Typography variant="body2">{company.industry ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Сайт
                </Typography>
                <Typography variant="body2">{company.website ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Адрес
                </Typography>
                <Typography variant="body2">{company.address ?? '—'}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Заметки
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {company.notes ?? '—'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <CrossEntityTabs tabs={tabs} activeId={activeTab} onChange={setActiveTab} />

      <Box sx={{ pt: 3 }}>
        {activeTab === 'overview' ? (
          <Typography variant="body2" color="text.secondary">
            Создано: {formatDate(company.createdAt)} · Версия: {company.version}
          </Typography>
        ) : null}

        {activeTab === 'contacts' ? (
          contactsQuery.isLoading ? (
            <LoadingSpinner />
          ) : contactsQuery.isError ? (
            <Alert severity="error">
              {contactsQuery.error instanceof Error ? contactsQuery.error.message : 'Не удалось загрузить контакты'}
            </Alert>
          ) : !contactsQuery.data || contactsQuery.data.content.length === 0 ? (
            <EmptyState title="Нет связанных контактов" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Имя</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(contactsQuery.data.content as Array<{ id: string; firstName: string; lastName: string; email: string | null }>).map((c) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/crm/contacts/${c.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {c.firstName} {c.lastName}
                    </TableCell>
                    <TableCell>{c.email ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}

        {activeTab === 'deals' ? (
          dealsQuery.isLoading ? (
            <LoadingSpinner />
          ) : dealsQuery.isError ? (
            <Alert severity="error">
              {dealsQuery.error instanceof Error ? dealsQuery.error.message : 'Не удалось загрузить сделки'}
            </Alert>
          ) : !dealsQuery.data || dealsQuery.data.content.length === 0 ? (
            <EmptyState title="Нет связанных сделок" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Стадия</TableCell>
                  <TableCell>Сумма</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dealsQuery.data.content as Deal[]).map((d) => (
                  <TableRow
                    key={d.id}
                    hover
                    onClick={() => navigate(`/crm/deals/${d.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{d.title}</TableCell>
                    <TableCell>
                      <DealStageChip stage={d.stage} />
                    </TableCell>
                    <TableCell>
                      <AmountChip amount={d.amount} currency={d.currency} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}

        {activeTab === 'payments' ? <PaymentBlock entityType="COMPANY" entityId={company.id} /> : null}
      </Box>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Редактировать компанию</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Название компании"
              required
              fullWidth
              value={editValues.name}
              onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
              inputProps={{ 'aria-label': 'Название компании' }}
            />
            <TextField
              label="Отрасль"
              fullWidth
              value={editValues.industry}
              onChange={(e) => setEditValues((v) => ({ ...v, industry: e.target.value }))}
              inputProps={{ 'aria-label': 'Отрасль' }}
            />
            <TextField
              label="ИНН"
              fullWidth
              value={editValues.taxId}
              onChange={(e) => setEditValues((v) => ({ ...v, taxId: e.target.value }))}
              inputProps={{ 'aria-label': 'ИНН' }}
            />
            <TextField
              label="Сайт"
              fullWidth
              value={editValues.website}
              onChange={(e) => setEditValues((v) => ({ ...v, website: e.target.value }))}
              onBlur={() =>
                // U-22: автопрефикс https:// при уходе с поля (edit-форма, канон §5.3).
                setEditValues((v) => ({ ...v, website: normalizeWebsite(v.website) }))
              }
              error={Boolean(websiteError)}
              helperText={websiteError ?? t('crm.companies.websiteHint')}
              inputProps={{ 'aria-label': 'Сайт' }}
            />
            <TextField
              label="Адрес"
              fullWidth
              value={editValues.address}
              onChange={(e) => setEditValues((v) => ({ ...v, address: e.target.value }))}
              inputProps={{ 'aria-label': 'Адрес' }}
            />
            <TextField
              label="Заметки"
              fullWidth
              multiline
              minRows={3}
              value={editValues.notes}
              onChange={(e) => setEditValues((v) => ({ ...v, notes: e.target.value }))}
              inputProps={{ 'aria-label': 'Заметки компании' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={editMutation.isPending || !editValues.name || Boolean(websiteError)}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
